import { useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDrag } from "react-dnd";

import { useHasPermission } from "../../../../common/Hooks";
import Utils from "../../../../utils/utils";
import Forms from "../../../../services/forms";
import PubSub from "../../../../services/PubSub";
import SectionItems from "../templates/SectionItems";
import { useSharedSection } from "../SharedSectionContext";
import Icon from "../../../controls/Icon";
import FORMS_DND_ITEMS from "../../../../config/forms-dnd";
import EditSection from "../templates/modals/EditSection";
import SharedSectionVersionHistory from "./modals/SharedSectionVersionHistory";
import PublishSharedSection from "./modals/PublishSharedSection";

const SharedSection = () => {
    const { id: deploymentId, objectSubTypeId: sharedSectionId } = useParams();
    const { sharedSection, setSharedSection, sharedSectionNames } = useSharedSection();
    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "forms"
    });
    const [translate] = useTranslation();

    const isReadOnly = !permissions.update || !sharedSection || sharedSection.isPublished;

    const [showEditSection, setShowEditSection] = useState(false);
    const [showSharedSectionVersionHistory, setShowSharedSectionVersionHistory] = useState(false);
    const [showPublishModal, setShowPublishModal] = useState(false);

    const getSharedSectionVersion = useCallback(async () => {
        if (sharedSectionId) {
            const res = await Forms.getSharedSectionVersion({
                deploymentId,
                sharedSectionId
            });
            if (!res.error && res.data) {
                setSharedSection(res.data);
            }
        }
    }, [setSharedSection, deploymentId, sharedSectionId]);

    const handlePublish = useCallback(async (values) => {
        const response = await Forms.publishSharedSectionVersion({
            deploymentId,
            sharedSectionId: sharedSection._id,
            body: values
        });

        if (!response.error) {
            PubSub.publish("show_info", translate("OTM_FORMS_SHARED_SECTIONS_PUBLISH_SUCCESS"), translate("OTM_INFO"));
        }

        await getSharedSectionVersion();
        PubSub.publish("refresh-shared-section-versions");

        setShowPublishModal(false);
    }, [deploymentId, sharedSection?._id, getSharedSectionVersion, translate]);

    useEffect(() => {
        if (sharedSection?._id !== sharedSectionId) {
            getSharedSectionVersion();
        }
    }, [sharedSection, sharedSectionId, getSharedSectionVersion]);

    if (!sharedSection) {
        return null;
    }

    return (
        <>
            <SectionHeader
                section={sharedSection}
                setShowEditSection={setShowEditSection}
                setShowSharedSectionVersionHistory={setShowSharedSectionVersionHistory}
                setShowPublishModal={setShowPublishModal}
                isReadOnly={isReadOnly}
            />
            <SectionItems
                sectionId={sharedSection._id}
                isShared={true}
                isReadOnly={isReadOnly || !permissions.update}
                refreshTemplate={async () => {
                    await getSharedSectionVersion();
                }}
                deletePermissions={permissions.delete}
            />
            <EditSection
                show={showEditSection}
                allowUpdate={!isReadOnly}
                isShared={true}
                isEdit={true}
                section={sharedSection}
                sectionNames={sharedSectionNames.filter((name) => name !== sharedSection.name)}
                onUpdate={async () => {
                    await getSharedSectionVersion();
                    setShowEditSection(false);
                }}
                onClose={() => {
                    setShowEditSection(false);
                }}
            />
            <SharedSectionVersionHistory
                show={showSharedSectionVersionHistory}
                onClose={() => {
                    setShowSharedSectionVersionHistory(false);
                }}
            />
            <PublishSharedSection
                show={showPublishModal}
                sharedSection={sharedSection}
                onUpdate={handlePublish}
                onClose={() => {
                    setShowPublishModal(false);
                }}
            />
        </>
    );
};

const SectionHeader = ({
    section,
    setShowEditSection,
    setShowSharedSectionVersionHistory,
    setShowPublishModal,
    isReadOnly
}) => {
    const [translate] = useTranslation();
    const [, drag] = useDrag(() => ({
        type: FORMS_DND_ITEMS.SHARED_SECTION,
        item: {
            ...(section || {}),
            _storage: {
                _id: section?._id
            },
            _dragging: {}
        }
    }), [section]);

    const publishButtonTitle = section.items.length === 0 ? "OTM_FORMS_SHARED_SECTION_PUBLISH_DISABLED_NO_ITEMS" : "OTM_FORMS_SHARED_SECTION_PUBLISH";

    return (
        <div ref={drag} className="card border border-secondary mb-3 moveable">
            <div className="card-body d-flex flex-column">
                <div className="row mt-n2 align-items-center justify-content-end">
                    <div className="col-auto mt-2 p-0 order-last order-lg-first">
                        <Icon icon="DragIndicatorOutlined" className="text-tertiary" />
                    </div>
                    <div className="col-12 col-lg mt-2 d-lg-flex align-items-lg-center">
                        {section.name}
                    </div>
                    <div className="col-auto mt-2">
                        <div className="row gx-2 align-items-center justify-content-end">
                            <div className="col-auto">
                                <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={() => {
                                        setShowSharedSectionVersionHistory(true);
                                    }}
                                >
                                    {translate("OTM_FORMS_SHARED_SECTIONS_MANAGE_VERSIONS")}
                                </Button>
                            </div>
                            {!isReadOnly ? <div className="col-auto ">
                                <Button
                                    size="sm"
                                    variant="success"
                                    onClick={() => {
                                        setShowPublishModal(true);
                                    }}
                                    disabled={section.items.length === 0}
                                    title={translate(publishButtonTitle)}
                                >{translate("OTM_FORMS_SHARED_SECTION_PUBLISH")}</Button>
                            </div> : null}
                            <div id="btn_edit_shared_section" className="col-auto">
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    className="p-0 border-0"
                                    onClick={() => {
                                        setShowEditSection(true);
                                    }}
                                    title={translate("OTM_EDIT")}
                                >
                                    <Icon icon="Settings" className="d-block" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SharedSection;